import * as React from 'react';
import styled from 'styled-components';
import {Detail, Icon} from '@forecast-it/design-system';

const Wrap = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[100]};
	min-width: 0;
`;
// Full-width and quiet, so the value leads and the caret is the only chrome. The row
// keeps its height between rest and edit, so nothing below it jumps when it opens.
const Trigger = styled.button`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
	width: 100%;
	border: 1px solid transparent;
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	background: transparent;
	padding: ${p => p.theme.sys.spacing[100]};
	margin: 0;
	cursor: pointer;
	text-align: left;
	min-width: 0;

	&:hover {
		background: ${p => p.theme.sys.color.background.disabled['1']};
	}
`;
const Caret = styled.span`
	display: inline-flex;
	margin-left: auto;
	flex: none;
`;
const Rest = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
	padding: ${p => p.theme.sys.spacing[100]};
	min-width: 0;
`;

interface Props {
	/** The field's name, e.g. `Assignee`. Also names the trigger for assistive tech. */
	label: string;
	/** Hide the resting label where the value already says what it is (a project chip). */
	showLabel?: boolean;
	/** True while the editing control is showing. */
	open: boolean;
	onOpen: () => void;
	/** The editing control. Rendered in place of the value while `open`. */
	editor: React.ReactNode;
	/** Nothing to press when the reader may not change this. */
	readOnly?: boolean;
	children: React.ReactNode;
	'data-cy'?: string;
}

/**
 * A Details-panel field that shows its value until pressed, then swaps in its editor.
 *
 * The panel is read far more often than it is edited, so at rest every field is just a
 * value; the caret is the only sign it can change. Swapping in place rather than
 * revealing a separate form means the value never moves, and there is no edit mode to
 * enter and leave for a single change.
 */
export function MetaField({
	label,
	showLabel = true,
	open,
	onOpen,
	editor,
	readOnly = false,
	children,
	'data-cy': dataCy,
}: Props) {
	return (
		<Wrap>
			{showLabel && <Detail color="medium">{label}</Detail>}
			{open ? (
				editor
			) : readOnly ? (
				<Rest data-cy={dataCy}>{children}</Rest>
			) : (
				<Trigger type="button" aria-label={`Change ${label.toLowerCase()}`} onClick={onOpen} data-cy={dataCy}>
					{children}
					<Caret>
						<Icon icon="caretDown" size="s" newColor="disabled" />
					</Caret>
				</Trigger>
			)}
		</Wrap>
	);
}
